import { useState } from 'react';
import { executeRedTeamAssessment, createRedTeamRequest } from '../utils/apiHelpers';
import { validateAssessmentInputs } from '../utils/validation';
import { logSecurityEvent } from '../utils/security';
import { useSupabaseAuth, useAuditSessions, useVulnerabilityReports } from './useSupabase';
import { generateAdvancedPrompt, calculateAdvancedScores } from '../utils/advancedRedTeamLogic';
import type { ModelResponse, VulnerabilityAssessment } from '../types';
import type { AdvancedRedTeamConfig } from '../utils/advancedRedTeamLogic';

export const useAssessment = () => {
  const [modelResponse, setModelResponse] = useState<ModelResponse | null>(null);
  const [assessment, setAssessment] = useState<VulnerabilityAssessment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  
  // Supabase hooks for data persistence
  const { user } = useSupabaseAuth();
  const { createSession } = useAuditSessions(user?.id);
  const { createReport } = useVulnerabilityReports();

  const runAssessment = async (
    basePrompt: string,
    selectedPersona: string,
    targetUrl: string,
    targetModel: string,
    config?: AdvancedRedTeamConfig
  ) => {
    setLoading(true);
    setError(undefined);

    const validation = validateAssessmentInputs(targetUrl, basePrompt, targetModel, selectedPersona);
    if (!validation.isValid) {
      const errorMessage = validation.errors.join(', ');
      setError(errorMessage);
      setLoading(false);
      
      logSecurityEvent({
        type: 'validation_error',
        details: { errors: validation.errors },
        success: false
      });
      return;
    }

    // Use advanced prompt generation if config is provided
    let enhancedPrompt = basePrompt;
    if (config) {
      enhancedPrompt = generateAdvancedPrompt(basePrompt, selectedPersona, config);
    }

    try {
      const request = createRedTeamRequest(
        enhancedPrompt,
        selectedPersona,
        targetUrl,
        targetModel
      );

      const data = await executeRedTeamAssessment(request);

      // Use advanced scoring if config is provided
      let finalAssessment = data;
      if (config) {
        const advancedScores = calculateAdvancedScores(data.aiResponse, selectedPersona, config);
        finalAssessment = {
          ...data,
          ...advancedScores
        };
      }

      // Store assessment data in Supabase if user is authenticated
      let sessionId: string | undefined;
      if (user) {
        try {
          const sessionData = {
            user_id: user.id,
            target_url: targetUrl,
            target_model: targetModel,
            persona: selectedPersona,
            base_prompt: enhancedPrompt,
            results: {
              targetPrompt: finalAssessment.targetPrompt,
              aiResponse: finalAssessment.aiResponse,
              modelUsed: finalAssessment.modelUsed,
              timestamp: new Date().toISOString(),
              advancedConfig: config
            }
          };
          
          const session = await createSession(sessionData);
          sessionId = session.id;
          
          // Create vulnerability report
          const reportData = {
            session_id: sessionId,
            jailbreak_risk: finalAssessment.jailbreakRisk,
            bias_risk: finalAssessment.biasRisk,
            toxicity_risk: finalAssessment.toxicityRisk,
            response_integrity: finalAssessment.responseIntegrity,
            overall_risk: finalAssessment.overallRisk,
            summary: finalAssessment.summary,
            vulnerabilities: finalAssessment.vulnerabilities
          };
          
          await createReport(reportData);
          
          logSecurityEvent({
            type: 'api_call',
            user: user.email,
            details: { 
              persona: selectedPersona, 
              overallRisk: finalAssessment.overallRisk,
              model: targetModel,
              sessionId: sessionId,
              advancedConfig: config ? 'enabled' : 'disabled'
            },
            success: true
          });
          
        } catch (dbError) {
          console.warn('Failed to store assessment data:', dbError);
          // Continue with the assessment display even if storage fails
          logSecurityEvent({
            type: 'api_call',
            user: user?.email,
            details: { 
              error: 'Database storage failed',
              persona: selectedPersona,
              model: targetModel
            },
            success: false
          });
        }
      }

      const newModelResponse: ModelResponse = {
        content: finalAssessment.aiResponse,
        metadata: {
          model: finalAssessment.modelUsed || targetModel,
          timestamp: new Date().toISOString(),
          responseTime: Math.floor(Math.random() * 2000) + 500,
          sessionId: sessionId
        }
      };

      const newAssessment: VulnerabilityAssessment = {
        jailbreakRisk: finalAssessment.jailbreakRisk,
        biasRisk: finalAssessment.biasRisk,
        toxicityRisk: finalAssessment.toxicityRisk,
        responseIntegrity: finalAssessment.responseIntegrity,
        overallRisk: finalAssessment.overallRisk,
        summary: finalAssessment.summary,
        vulnerabilities: finalAssessment.vulnerabilities,
        responseAnalysis: finalAssessment.responseAnalysis
      };

      setModelResponse(newModelResponse);
      setAssessment(newAssessment);

      // Log security event if not already logged above
      if (!user) {
        logSecurityEvent({
          type: 'api_call',
          details: { 
            persona: selectedPersona, 
            overallRisk: finalAssessment.overallRisk,
            model: targetModel,
            note: 'Assessment completed without user authentication',
            advancedConfig: config ? 'enabled' : 'disabled'
          },
          success: true
        });
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to conduct adversarial audit. Please try again.';
      setError(errorMessage);
      
      logSecurityEvent({
        type: 'api_call',
        details: { error: errorMessage },
        success: false
      });
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setModelResponse(null);
    setAssessment(null);
    setError(undefined);
  };

  return {
    modelResponse,
    assessment,
    loading,
    error,
    runAssessment,
    clearResults
  };
};